import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Lock,
  Shield,
  Search,
  Loader2,
  MessageCircle,
  Mail,
  Phone,
  Video,
  Database,
  EyeOff,
  Users,
  Bell,
  Smartphone,
  Send,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { ValidationTab } from './PrivacyValidationTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHANNEL_META = {
  whatsapp: { icon: MessageCircle, label: 'WhatsApp', color: 'text-emerald-600' },
  email:    { icon: Mail,          label: 'Email',    color: 'text-sky-600' },
  voice:    { icon: Phone,         label: 'Voix',     color: 'text-violet-600' },
  meeting:  { icon: Video,         label: 'Visio',    color: 'text-amber-600' },
  crm:      { icon: Database,      label: 'CRM',      color: 'text-slate-600' },
};

function StatTile({ value, label, accent, testid }) {
  const accentMap = {
    private:  'text-[#DC2626]',
    ingested: 'text-[#16A34A]',
    pending:  'text-[#EA580C]',
    neutral:  'text-foreground',
  };
  return (
    <Card className="border-border/60" data-testid={testid}>
      <CardContent className="p-4">
        <div className={`text-2xl font-heading font-bold tracking-tight ${accentMap[accent] || accentMap.neutral}`}>
          {value}
        </div>
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground mt-1">
          {label}
        </div>
      </CardContent>
    </Card>
  );
}

function ContactRow({ contact, onToggle, busy }) {
  const isPrivate = contact.privacy?.mode === 'private';
  const fullName = `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || '(Sans nom)';
  const handle = contact.email || contact.phone || '—';

  return (
    <div
      className={`flex items-center justify-between gap-3 px-4 py-3 border-b last:border-b-0 transition-colors ${
        isPrivate ? 'bg-red-50/40 dark:bg-red-950/20' : 'hover:bg-muted/40'
      }`}
      data-testid={`privacy-contact-${contact.id}`}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
          isPrivate ? 'bg-red-100 dark:bg-red-950/50' : 'bg-muted'
        }`}>
          {isPrivate
            ? <EyeOff className="w-4 h-4 text-[#DC2626]" />
            : <Shield className="w-4 h-4 text-muted-foreground" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-medium truncate">{fullName}</span>
            {contact.company && (
              <span className="text-xs text-muted-foreground truncate">· {contact.company}</span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs text-muted-foreground truncate">{handle}</span>
            <div className="flex items-center gap-1">
              {(contact.channels || []).map((ch) => {
                const meta = CHANNEL_META[ch];
                if (!meta) return null;
                const Icon = meta.icon;
                return (
                  <Icon
                    key={ch}
                    className={`w-3 h-3 ${meta.color}`}
                    aria-label={meta.label}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {isPrivate ? (
          <Badge className="bg-[#DC2626] hover:bg-[#DC2626] text-white text-[10px]">Privé</Badge>
        ) : (
          <Badge variant="outline" className="text-[10px] text-[#16A34A] border-[#16A34A]/40">Ingéré</Badge>
        )}
        <Switch
          checked={!isPrivate}
          disabled={busy}
          onCheckedChange={(checked) => onToggle(contact, checked ? 'ingest' : 'private')}
          data-testid={`privacy-toggle-${contact.id}`}
        />
      </div>
    </div>
  );
}

function ContactsTab({ token, refreshStats }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filterMode, setFilterMode] = useState('all'); // all | ingest | private
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const fetchContacts = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = { page: p, per_page: 50 };
      if (search.trim()) params.search = search.trim();
      if (filterMode !== 'all') params.mode = filterMode;
      const res = await axios.get(`${API}/privacy/contacts`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setPage(res.data.page || 1);
    } catch (e) {
      toast.error('Impossible de charger les contacts');
    } finally {
      setLoading(false);
    }
  }, [token, search, filterMode]);

  useEffect(() => {
    fetchContacts(1);
  }, [fetchContacts]);

  const handleToggle = async (contact, newMode) => {
    setBusyId(contact.id);
    try {
      await axios.patch(
        `${API}/privacy/contacts/${contact.id}`,
        { mode: newMode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setItems((prev) =>
        prev.map((c) =>
          c.id === contact.id ? { ...c, privacy: { ...(c.privacy || {}), mode: newMode } } : c
        )
      );
      refreshStats?.();
      toast.success(
        newMode === 'private'
          ? `${contact.first_name || 'Contact'} passé en privé — plus aucun message ne sera ingéré`
          : `${contact.first_name || 'Contact'} réactivé`
      );
    } catch (e) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-3" data-testid="privacy-contacts-tab">
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher un contact (nom, email, téléphone, société)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="privacy-contact-search"
          />
        </div>
        <div className="flex gap-1">
          {[
            { k: 'all',     label: `Tous (${total})` },
            { k: 'ingest',  label: 'Ingérés' },
            { k: 'private', label: 'Privés' },
          ].map((f) => (
            <Button
              key={f.k}
              size="sm"
              variant={filterMode === f.k ? 'default' : 'outline'}
              onClick={() => setFilterMode(f.k)}
              data-testid={`privacy-filter-${f.k}`}
            >
              {f.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden bg-card max-h-[55vh] overflow-y-auto">
        {loading && items.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement...
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground text-sm">
            Aucun contact trouvé
          </div>
        ) : (
          items.map((c) => (
            <ContactRow
              key={c.id}
              contact={c}
              onToggle={handleToggle}
              busy={busyId === c.id}
            />
          ))
        )}
      </div>

      {total > 50 && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Page {page} — {total} contacts</span>
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="outline"
              disabled={page <= 1 || loading}
              onClick={() => fetchContacts(page - 1)}
              data-testid="privacy-prev-page"
            >
              Précédent
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={page * 50 >= total || loading}
              onClick={() => fetchContacts(page + 1)}
              data-testid="privacy-next-page"
            >
              Suivant
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function GroupsTab({ token }) {
  // Phase 1 placeholder pointing to the existing groups admin page if any
  return (
    <div className="text-center py-10 text-sm text-muted-foreground" data-testid="privacy-groups-tab">
      <Users className="w-8 h-8 mx-auto mb-3 opacity-50" />
      <p>La gestion détaillée des groupes WhatsApp est disponible dans</p>
      <p className="font-medium text-foreground mt-1">Paramètres → WhatsApp → Groupes</p>
      <p className="text-xs mt-3 max-w-md mx-auto">
        Astuce : un nouveau groupe est désormais détecté automatiquement
        sans avoir besoin de reconnecter WhatsApp.
      </p>
    </div>
  );
}

function NotificationsTab({ token }) {
  const [devices, setDevices] = useState([]);
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [devicesRes, prefsRes] = await Promise.all([
        axios.get(`${API}/devices`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/notifications/preferences`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setDevices(Array.isArray(devicesRes.data) ? devicesRes.data : []);
      setPrefs(prefsRes.data?.preferences || {});
    } catch (e) {
      toast.error('Impossible de charger les préférences');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const sendTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await axios.post(
        `${API}/notifications/test`,
        { title: 'Test cloneIA', body: 'Voici une notification de test. ✅' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const r = res.data || {};
      setTestResult(r);
      if (r.sent > 0) {
        toast.success(`Notification envoyée à ${r.sent} appareil(s)`);
      } else if (r.reason === 'no_active_device') {
        toast.warning('Aucun appareil mobile actif n’est enregistré');
      } else {
        toast.warning('Aucune notification envoyée');
      }
    } catch (e) {
      toast.error('Erreur lors de l’envoi du test');
    } finally {
      setTesting(false);
    }
  };

  const togglePref = async (key, value) => {
    try {
      const res = await axios.patch(
        `${API}/notifications/preferences`,
        { [key]: value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPrefs(res.data?.preferences || {});
      toast.success('Préférence mise à jour');
    } catch (e) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const removeDevice = async (deviceId) => {
    try {
      await axios.delete(`${API}/devices/${deviceId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDevices((prev) => prev.filter((d) => d.id !== deviceId));
      toast.success('Appareil retiré');
    } catch (e) {
      toast.error('Impossible de retirer l’appareil');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement...
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="privacy-notifications-tab">
      {/* Devices */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-heading font-semibold uppercase tracking-wider text-muted-foreground">
            Appareils enregistrés ({devices.length})
          </h3>
          <Button
            size="sm"
            onClick={sendTest}
            disabled={testing || devices.length === 0}
            data-testid="privacy-send-test-push"
          >
            {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Envoyer un test
          </Button>
        </div>
        {testResult && (
          <div className="mb-3 text-xs bg-muted/50 rounded px-3 py-2">
            Résultat : {testResult.sent} envoyé(s), {testResult.inactive_count || 0} marqué(s) inactif(s)
            {testResult.reason && ` — ${testResult.reason}`}
          </div>
        )}
        {devices.length === 0 ? (
          <div className="text-center py-8 text-sm text-muted-foreground border rounded-lg">
            <Smartphone className="w-8 h-8 mx-auto mb-2 opacity-50" />
            Aucun appareil mobile enregistré. <br />
            Ouvre l’app mobile cloneIA pour recevoir tes premières notifications.
          </div>
        ) : (
          <div className="border rounded-lg overflow-hidden bg-card">
            {devices.map((d) => (
              <div
                key={d.id}
                className="flex items-center justify-between gap-3 px-4 py-3 border-b last:border-b-0"
                data-testid={`privacy-device-${d.id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Smartphone className="w-4 h-4 text-muted-foreground shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">
                      {d.device_name || (d.platform === 'ios' ? 'iPhone' : d.platform === 'android' ? 'Android' : 'Web')}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {d.platform?.toUpperCase()} · {(d.push_token || '').slice(0, 24)}…
                    </div>
                  </div>
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => removeDevice(d.id)}
                  className="text-[#DC2626] hover:text-[#DC2626]"
                  data-testid={`privacy-device-remove-${d.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Preferences */}
      <section>
        <h3 className="text-sm font-heading font-semibold uppercase tracking-wider text-muted-foreground mb-3">
          Préférences
        </h3>
        <div className="border rounded-lg bg-card">
          {[
            {
              k: 'memory_created',
              label: 'Nouvelle mémoire ingérée',
              desc: 'Recevoir une notif à chaque nouvel appel / email / message WhatsApp ajouté à la mémoire de tes clones',
            },
            {
              k: 'pending_validation',
              label: 'Conversation en attente de validation',
              desc: 'Recevoir une notif quand un contact inconnu écrit et que le mode validation est actif (Phase 3)',
            },
            {
              k: 'whatsapp_disconnected',
              label: 'WhatsApp déconnecté',
              desc: 'Recevoir une alerte quand une session WhatsApp reste déconnectée plus de 10 min',
            },
            {
              k: 'daily_digest',
              label: 'Récap quotidien',
              desc: 'Un seul résumé par jour le matin plutôt que des notifs au fil de l’eau',
            },
          ].map((p) => (
            <div
              key={p.k}
              className="flex items-start justify-between gap-4 px-4 py-3 border-b last:border-b-0"
              data-testid={`privacy-pref-row-${p.k}`}
            >
              <div className="min-w-0">
                <div className="text-sm font-medium">{p.label}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{p.desc}</div>
              </div>
              <Switch
                checked={!!prefs?.[p.k]}
                onCheckedChange={(v) => togglePref(p.k, v)}
                data-testid={`privacy-pref-toggle-${p.k}`}
              />
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export function PrivacyCenter({ open, onOpenChange, token }) {
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  const fetchStats = useCallback(async () => {
    if (!token) return;
    setLoadingStats(true);
    try {
      const res = await axios.get(`${API}/privacy/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(res.data);
    } catch (e) {
      // silent
    } finally {
      setLoadingStats(false);
    }
  }, [token]);

  useEffect(() => {
    if (open) fetchStats();
  }, [open, fetchStats]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[95vw] max-w-3xl p-0 overflow-hidden max-h-[90vh] flex flex-col"
        data-testid="privacy-center-modal"
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2 font-heading text-2xl tracking-tight">
            <Lock className="w-6 h-6 text-[#DC2626]" />
            Vie privée
          </DialogTitle>
          <DialogDescription className="text-xs uppercase tracking-wider text-muted-foreground">
            Contrôle ce que ton clone apprend — et oublie
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-4 grid grid-cols-2 sm:grid-cols-4 gap-3 bg-muted/30 shrink-0">
          <StatTile
            value={loadingStats ? '…' : (stats?.contacts_total ?? 0).toLocaleString('fr-FR')}
            label="Contacts"
            accent="neutral"
            testid="privacy-stat-total"
          />
          <StatTile
            value={loadingStats ? '…' : (stats?.contacts_ingested ?? 0).toLocaleString('fr-FR')}
            label="Ingérés"
            accent="ingested"
            testid="privacy-stat-ingested"
          />
          <StatTile
            value={loadingStats ? '…' : (stats?.contacts_private ?? 0).toLocaleString('fr-FR')}
            label="Privés"
            accent="private"
            testid="privacy-stat-private"
          />
          <StatTile
            value={loadingStats ? '…' : (stats?.pending_validation ?? 0).toLocaleString('fr-FR')}
            label="En attente"
            accent="pending"
            testid="privacy-stat-pending"
          />
        </div>

        <div className="px-6 pb-6 overflow-y-auto flex-1">
          <Tabs defaultValue="contacts" className="w-full">
            <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 mb-4 h-auto gap-1">
              <TabsTrigger value="contacts" className="text-xs sm:text-sm" data-testid="privacy-tab-contacts">
                Contacts
              </TabsTrigger>
              <TabsTrigger value="validation" className="text-xs sm:text-sm relative" data-testid="privacy-tab-validation">
                À valider
                {stats?.pending_validation > 0 && (
                  <span className="ml-1.5 inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold rounded-full bg-[#EA580C] text-white">
                    {stats.pending_validation}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="groups" className="text-xs sm:text-sm" data-testid="privacy-tab-groups">
                Groupes WA
              </TabsTrigger>
              <TabsTrigger value="notifications" className="text-xs sm:text-sm" data-testid="privacy-tab-notifications">
                <Bell className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Notifications</span>
                <span className="sm:hidden">Notifs</span>
              </TabsTrigger>
            </TabsList>
            <TabsContent value="contacts">
              <ContactsTab token={token} refreshStats={fetchStats} />
            </TabsContent>
            <TabsContent value="validation">
              <ValidationTab token={token} refreshStats={fetchStats} />
            </TabsContent>
            <TabsContent value="groups">
              <GroupsTab token={token} />
            </TabsContent>
            <TabsContent value="notifications">
              <NotificationsTab token={token} />
            </TabsContent>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default PrivacyCenter;
